// Administración de usuarios — solo propietario (Tarea 19C, Req. 19.2–19.6).
//
// Invitar usuario por email, ver/editar la matriz de permisos por funcionalidad
// (none/read/read_write), aplicar el preset "solo lectura de catálogo y wishlist"
// y revocar acceso.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AlertMessage, AsyncBoundary, Badge, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';
import type { AdminUser, Feature, PermissionLevel, PermissionPreset } from '../types.js';

/** Funcionalidades administrables (excepto la propia administración). */
const FEATURES: Feature[] = [
  'catalog', 'wishlist', 'duplicates', 'top', 'locations',
  'ingestion', 'sync', 'quick_lookup', 'deal_hunter',
];

const LEVELS: PermissionLevel[] = ['none', 'read', 'read_write'];

/** Preset "solo lectura de catálogo y wishlist" (Req. 19.5). */
const READ_ONLY_PRESET: Partial<Record<Feature, PermissionLevel>> = {
  catalog: 'read',
  wishlist: 'read',
};

export function AdminView() {
  const { t } = useTranslation();
  const api = useApi();
  const users = useAsync(() => api.admin.listUsers(), []);
  const presets = useAsync(() => api.admin.presets(), []);
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Edición de nombre en línea y borrado definitivo (por usuario).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const presetList: PermissionPreset[] = presets.data?.items ?? [];

  function startEdit(user: AdminUser) {
    setEditingId(user.id);
    setEditName(user.display_name ?? '');
    setError(null);
    setMessage(null);
  }

  async function saveName(user: AdminUser) {
    setSavingId(user.id);
    setError(null);
    try {
      await api.admin.updateUser(user.id, { display_name: editName.trim() || null });
      setMessage(t('admin.msgNameUpdated', { email: user.email }));
      setEditingId(null);
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errUpdateName'));
    } finally {
      setSavingId(null);
    }
  }

  async function deleteForever(user: AdminUser) {
    if (!confirm(t('admin.deleteForeverConfirm', { email: user.email }))) return;
    try {
      await api.admin.deleteUser(user.id);
      setMessage(t('admin.msgUserDeleted', { email: user.email }));
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errDeleteUser'));
    }
  }

  async function applyProfile(user: AdminUser, preset: PermissionPreset) {
    try {
      await api.admin.setPermissions(user.id, preset.permissions);
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errApplyProfile'));
    }
  }

  async function invite(preset: boolean) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.admin.invite({ email: inviteEmail.trim(), permissions: preset ? READ_ONLY_PRESET : undefined });
      setMessage(t('admin.msgInvited', { email: inviteEmail }));
      setInviteEmail('');
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errInvite'));
    } finally {
      setBusy(false);
    }
  }

  async function setLevel(user: AdminUser, feature: Feature, level: PermissionLevel) {
    try {
      await api.admin.setPermissions(user.id, { [feature]: level });
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errSetPermission'));
    }
  }

  async function applyPreset(user: AdminUser) {
    try {
      await api.admin.setPermissions(user.id, READ_ONLY_PRESET);
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errApplyPreset'));
    }
  }

  async function revoke(user: AdminUser) {
    if (!confirm(t('admin.revokeConfirm', { email: user.email }))) return;
    try {
      await api.admin.revoke(user.id);
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.errRevoke'));
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('admin.pageTitle')}
        subtitle={t('admin.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      <AlertMessage message={error} />
      {message && (
        <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
          {message}
        </div>
      )}

      <Card title={t('admin.inviteTitle')} icon="person_add">
        <div className="flex flex-col gap-space-xs">
          <label className="field-label">{t('admin.emailLabel')}</label>
          <div className="field-slot">
            <Icon name="mail" className="text-on-surface-variant text-[18px] mr-space-xs" />
            <input className="field-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={t('admin.emailPlaceholder')} />
          </div>
        </div>
        <div className="flex flex-col gap-space-sm mt-space-sm">
          <button className="btn-primary w-full" type="button" disabled={busy || !inviteEmail.trim()} onClick={() => void invite(false)}>
            {busy ? <ButtonSpinner /> : <Icon name="send" className="text-[18px]" />} {busy ? t('admin.inviting') : t('admin.invite')}
          </button>
          <button className="btn-secondary w-full" type="button" disabled={busy || !inviteEmail.trim()} onClick={() => void invite(true)}>
            <Icon name="visibility" className="text-[18px]" /> {t('admin.inviteReadOnly')}
          </button>
        </div>
      </Card>

      <AsyncBoundary state={users} empty={(d) => d.items.length === 0}>
        {(d) => (
          <>
            {d.items.map((user) => (
              <Card
                key={user.id}
                title={user.display_name ?? user.email}
                icon={user.role === 'owner' ? 'shield_person' : 'person'}
                action={
                  user.role === 'system_admin' ? (
                    <Badge tone="info">{t('admin.roleSystemAdmin')}</Badge>
                  ) : user.role === 'owner' ? (
                    <Badge tone="info">{t('admin.roleOwner')}</Badge>
                  ) : user.active ? (
                    <Badge tone="success">{t('admin.roleActive')}</Badge>
                  ) : (
                    <Badge tone="danger">{t('admin.roleRevoked')}</Badge>
                  )
                }
              >
                {user.role === 'owner' || user.role === 'system_admin' ? (
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {user.role === 'system_admin'
                      ? t('admin.systemAdminFullAccess')
                      : t('admin.ownerFullAccess')}
                  </p>
                ) : (
                  <>
                    <p className="font-code-catalog text-body-sm text-on-surface-variant mb-space-sm">{user.email}</p>

                    {/* Edición del nombre visible */}
                    {editingId === user.id ? (
                      <div className="flex flex-col gap-space-sm mb-space-md">
                        <div className="field-slot">
                          <Icon name="badge" className="text-on-surface-variant text-[18px] mr-space-xs" />
                          <input
                            className="field-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder={t('admin.visibleNamePlaceholder')}
                          />
                        </div>
                        <div className="flex items-center gap-space-sm">
                          <button
                            className="btn-primary flex-1"
                            type="button"
                            disabled={savingId === user.id}
                            onClick={() => void saveName(user)}
                          >
                            {savingId === user.id ? <ButtonSpinner /> : <Icon name="check" className="text-[18px]" />} {t('admin.save')}
                          </button>
                          <button className="btn-ghost" type="button" onClick={() => setEditingId(null)}>
                            <Icon name="close" className="text-[18px]" /> {t('admin.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-space-md">
                        <button className="btn-secondary btn-sm" type="button" onClick={() => startEdit(user)}>
                          <Icon name="edit" className="text-[16px]" /> {t('admin.editName')}
                        </button>
                      </div>
                    )}

                    {/* Matriz de permisos: segmented control por funcionalidad */}
                    <div className="flex flex-col gap-space-xs">
                      {FEATURES.map((f) => {
                        const current = user.permissions?.[f] ?? 'none';
                        return (
                          <div key={f} className="flex items-center justify-between gap-space-sm surface-rack-card !p-space-xs !py-space-sm">
                            <span className="font-body-sm text-body-sm text-on-surface truncate">{t(`admin.feature.${f}`)}</span>
                            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-surface-base shrink-0">
                              {LEVELS.map((l) => (
                                <button
                                  key={l}
                                  type="button"
                                  onClick={() => void setLevel(user, f, l)}
                                  className={`px-2 h-7 rounded font-badge-micro text-badge-micro uppercase transition-colors ${
                                    current === l
                                      ? l === 'read_write'
                                        ? 'bg-goldmine-mint/20 text-goldmine-mint'
                                        : l === 'read'
                                          ? 'bg-primary-container/20 text-primary'
                                          : 'bg-surface-container text-on-surface-variant'
                                      : 'text-on-surface-variant'
                                  }`}
                                >
                                  {t(`admin.level.${l}`)}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Perfiles: aplican un mapa de permisos completo con un clic. */}
                    {presetList.length > 0 && (
                      <div className="flex flex-col gap-space-xs mt-space-md">
                        <span className="field-label">{t('admin.applyProfile')}</span>
                        <div className="flex flex-wrap gap-space-xs">
                          {presetList.map((p) => (
                            <button
                              key={p.name}
                              type="button"
                              className="btn-secondary btn-sm"
                              onClick={() => void applyProfile(user, p)}
                            >
                              <Icon name="badge" className="text-[16px]" /> {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-space-sm mt-space-md">
                      <button className="btn-secondary flex-1" type="button" onClick={() => void applyPreset(user)}>
                        <Icon name="preview" className="text-[18px]" /> {t('admin.presetRead')}
                      </button>
                      <button className="btn-ghost !text-priority-urgent" type="button" onClick={() => void revoke(user)}>
                        <Icon name="block" className="text-[18px]" /> {t('admin.revoke')}
                      </button>
                    </div>
                    {/* Borrado definitivo (fila + Cognito), distinto de "Revocar"
                        (baja lógica reversible). */}
                    <div className="mt-space-sm">
                      <button
                        className="btn-ghost btn-sm !text-priority-urgent"
                        type="button"
                        onClick={() => void deleteForever(user)}
                      >
                        <Icon name="delete_forever" className="text-[16px]" /> {t('admin.deleteForever')}
                      </button>
                    </div>
                  </>
                )}
              </Card>
            ))}
          </>
        )}
      </AsyncBoundary>
      </div>
    </div>
  );
}
